import { z } from 'zod';
import { VIAS_ADMINISTRACION } from '../catalogos/documentos.js';
import { esquemaId, esquemaTextoClinico } from './comunes.js';
/**
 * Receta.
 *
 * Es un documento firmado, como la historia y el estudio: se emite, se firma con
 * la cedula de quien la extiende, y si algo esta mal no se edita en silencio
 * —sube de version y se vuelve a firmar con un motivo—.
 */
/**
 * Un medicamento de la receta.
 *
 * `nombre` y `dosis` son lo unico obligatorio. Lo demas se llena cuando hace
 * falta: una receta con seis campos obligatorios por medicamento se rellena con
 * puntos y guiones, y entonces los campos no dicen nada.
 */
export const esquemaMedicamento = z.object({
    nombre: z.string().trim().min(2, 'Escriba el medicamento').max(160),
    presentacion: esquemaTextoClinico(120).default(''),
    dosis: z.string().trim().min(1, 'Indique la dosis').max(120),
    via: z.enum(VIAS_ADMINISTRACION).default('Oral'),
    frecuencia: esquemaTextoClinico(120).default(''),
    duracion: esquemaTextoClinico(120).default(''),
    indicaciones: esquemaTextoClinico(500).default(''),
});
export const esquemaCrearReceta = z.object({
    pacienteId: esquemaId,
    /** Fecha clinica de la receta, como texto: no tiene hora. */
    fecha: z
        .string()
        .trim()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use el formato AAAA-MM-DD'),
    diagnostico: esquemaTextoClinico(500).default(''),
    /** De donde sale esta receta, si sale de un estudio del expediente. */
    colposcopiaId: esquemaId.optional(),
    medicamentos: z
        .array(esquemaMedicamento)
        .min(1, 'Una receta sin medicamentos no es una receta')
        .max(20),
    indicacionesGenerales: esquemaTextoClinico(2000).default(''),
    /**
     * Alergias que se confirmaron al extender la receta.
     *
     * Se copia aqui a proposito, en lugar de leerla del paciente al imprimir: la
     * receta es un documento de un momento, y lo que importa es que se pregunto
     * entonces. Si mañana se registra una alergia nueva, la receta de ayer sigue
     * diciendo lo que se sabia ayer.
     */
    alergiasConfirmadas: esquemaTextoClinico(500).default(''),
});
export const esquemaActualizarReceta = esquemaCrearReceta
    .omit({ pacienteId: true })
    .partial()
    .extend({
    /** Corregir un documento firmado exige decir por que. */
    motivoEdicion: z.string().trim().min(5, 'Explique qué se corrige y por qué').max(300),
});
export const esquemaListarRecetas = z.object({
    pacienteId: esquemaId.optional(),
    pagina: z.coerce.number().int().min(1).default(1),
    porPagina: z.coerce.number().int().min(1).max(100).default(20),
});
/** Como se lee una receta en una linea: «Metronidazol 500 mg · Oral · c/8 h». */
export function medicamentoEnUnaLinea(medicamento) {
    return [medicamento.nombre, medicamento.dosis, medicamento.via, medicamento.frecuencia]
        .map((parte) => parte.trim())
        .filter(Boolean)
        .join(' · ');
}
//# sourceMappingURL=receta.js.map